/**
 * JoinUsExpander
 * -----------------------------------------------------------------------------
 * Disclosure widget for the public nav. Default state: single "JOIN US" pill.
 * Expanded state: "NEW AGENT" + "NEW CLIENT" pills rendered inline with a
 * framer-motion width+opacity morph. Collapses on outside click, Escape, or
 * after navigating via either option.
 *
 * `variant="nav"` is the compact nav pill. `variant="drawer"` is the mobile
 * drawer variant — same behavior, larger tap targets.
 */

import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

interface JoinUsExpanderProps {
    variant?: 'nav' | 'drawer';
    className?: string;
}

const EASE: [number, number, number, number] = [0.32, 0.72, 0, 1];

const AGENT_HREF = '/agents/become-an-agent/apply';
const CLIENT_HREF = '/clients';

export default function JoinUsExpander({ variant = 'nav', className = '' }: JoinUsExpanderProps) {
    const [expanded, setExpanded] = useState(false);
    const prefersReducedMotion = useReducedMotion() ?? false;
    const containerRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const revealedRef = useRef<HTMLDivElement>(null);
    const shouldRefocusRef = useRef(false);
    const groupId = useId();

    const close = useCallback((restoreFocus = false) => {
        if (restoreFocus) shouldRefocusRef.current = true;
        setExpanded(false);
    }, []);

    // Fires after the revealed group's exit animation completes and the toggle button has re-mounted
    const handleExitComplete = useCallback(() => {
        if (shouldRefocusRef.current) {
            shouldRefocusRef.current = false;
            // rAF lets React commit the re-mounted toggle before we focus it
            requestAnimationFrame(() => toggleRef.current?.focus());
        }
    }, []);

    useEffect(() => {
        if (!expanded) return;
        const handler = (e: MouseEvent) => {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target as Node)) close(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [expanded, close]);

    useEffect(() => {
        if (!expanded) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close(true);
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [expanded, close]);

    useEffect(() => {
        if (!expanded) return;
        const id = window.setTimeout(
            () => {
                const first = revealedRef.current?.querySelector<HTMLAnchorElement>('a[data-join-option="agent"]');
                first?.focus();
            },
            prefersReducedMotion ? 0 : 200,
        );
        return () => window.clearTimeout(id);
    }, [expanded, prefersReducedMotion]);

    const isNav = variant === 'nav';

    const togglePill = cn(
        'lux-caps lux-caps-sm rounded-sm border border-[#F5F5F5]/30 bg-[#0b2222] text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 focus-visible:ring-2 focus-visible:ring-[#F5F5F5]/60 focus-visible:outline-none',
        isNav ? 'px-4 py-1' : 'px-5 py-2.5 text-sm',
    );

    const agentPill = cn(
        'lux-caps lux-caps-sm inline-flex items-center rounded-sm border border-[#F5F5F5]/30 bg-[#0b2222] text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 focus-visible:ring-2 focus-visible:ring-[#F5F5F5]/60 focus-visible:outline-none',
        isNav ? 'px-4 py-1' : 'px-5 py-2.5 text-sm',
    );

    const clientPill = cn(
        'lux-caps lux-caps-sm inline-flex items-center rounded-sm border border-[#F5F5F5]/30 bg-[#F5F5F5]/10 text-[#F5F5F5] transition-colors duration-300 hover:bg-[#F5F5F5]/20 focus-visible:ring-2 focus-visible:ring-[#F5F5F5]/60 focus-visible:outline-none',
        isNav ? 'px-4 py-1' : 'px-5 py-2.5 text-sm',
    );

    const morphTransition = prefersReducedMotion ? { duration: 0 } : { duration: 0.3, ease: EASE };

    return (
        <div ref={containerRef} className={cn('relative inline-flex items-center', className)}>
            <AnimatePresence initial={false} mode="wait" onExitComplete={handleExitComplete}>
                {!expanded ? (
                    <motion.div key="toggle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={morphTransition}>
                        <button
                            ref={toggleRef}
                            type="button"
                            onClick={() => setExpanded(true)}
                            aria-expanded={false}
                            aria-controls={groupId}
                            className={togglePill}
                        >
                            Get Started
                        </button>
                    </motion.div>
                ) : (
                    <motion.div
                        key="revealed"
                        ref={revealedRef}
                        id={groupId}
                        role="group"
                        aria-label="Get Started options"
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={morphTransition}
                        className="flex items-center gap-2 overflow-hidden"
                    >
                        <Link href={AGENT_HREF} onClick={() => close(false)} data-join-option="agent" className={agentPill}>
                            New Agent
                        </Link>
                        <Link href={CLIENT_HREF} onClick={() => close(false)} data-join-option="client" className={clientPill}>
                            New Client
                        </Link>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
